import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { listPublicBanners } from '@/api/contentService';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { Bell, ExternalLink, Calendar } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useSearchParams } from 'react-router-dom';

export default function Avisos() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: banners = [], isLoading } = useQuery({
    queryKey: ['allBanners'],
    queryFn: () => listPublicBanners(50)
  });
  const selectedBanner = banners.find((banner) => String(banner.id) === searchParams.get('bannerId'));
  const showBanner = (id) => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('bannerId', String(id));
    setSearchParams(nextParams);
  };
  const closeBanner = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('bannerId');
    setSearchParams(nextParams, { replace: true });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader title="Avisos" subtitle="Comunicados importantes" showBack={true} />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {banners.length === 0 ? (
          <EmptyState
            icon={Bell}
            title="Nenhum aviso disponível"
            description="Não há avisos no momento."
          />
        ) : (
          <div className="space-y-4">
            {banners.map((banner, index) => (
              <motion.div
                key={banner.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white rounded-2xl shadow-sm overflow-hidden ${selectedBanner?.id === banner.id ? 'mbv-banner-selected' : ''}`}
              >
                {banner.image_url && (
                  <button type="button" className="aspect-video relative block w-full" onClick={() => showBanner(banner.id)} aria-label={`Abrir banner ${banner.title || ''}`}>
                    <img
                      src={banner.image_url}
                      alt={banner.title}
                      className="w-full h-full object-cover"
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                )}
                <div className="p-4">
                  <h3 className="font-semibold text-gray-900 text-lg"><button type="button" onClick={() => showBanner(banner.id)} className="text-left">{banner.title}</button></h3>
                  {banner.description && (
                    <p className="text-gray-600 mt-2">{banner.description}</p>
                  )}
                  
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Calendar className="w-4 h-4" />
                      {format(new Date(banner.created_date), "d 'de' MMM", { locale: ptBR })}
                    </div>
                    {banner.link && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => window.open(banner.link, '_blank')}
                      >
                        Saiba mais
                        <ExternalLink className="w-3 h-3 ml-1" />
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={Boolean(selectedBanner)} onOpenChange={(open) => { if (!open) closeBanner(); }}>
        <DialogContent className="mbv-banner-detail-dialog max-w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-auto p-4 sm:p-6">
          {selectedBanner && (
            <>
              <DialogHeader><DialogTitle>{selectedBanner.title || 'Aviso'}</DialogTitle></DialogHeader>
              {selectedBanner.image_url && (
                <img src={selectedBanner.image_url} alt={selectedBanner.title || 'Banner'} className="mbv-banner-detail-image" />
              )}
              {selectedBanner.description && <p className="mbv-banner-detail-description">{selectedBanner.description}</p>}
              <div className="mbv-banner-detail-actions">
                {selectedBanner.signup_enabled && (
                  <a href={selectedBanner.signup_link || '/inscricao-evento'} target="_blank" rel="noopener noreferrer"><Button>Faça sua inscrição</Button></a>
                )}
                {selectedBanner.link && (
                  <a href={selectedBanner.link} target="_blank" rel="noopener noreferrer"><Button variant="outline">Saiba mais <ExternalLink className="w-4 h-4 ml-2" /></Button></a>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
